/**
 * Frontend script for the GlaShutterSlider block.
 * This script initializes the slider logic, including navigation (prev/next),
 * pagination, dynamic autoplay, and parallax effects. It handles multiple
 * instances of the slider on a single page.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */
import domReady from "@wordpress/dom-ready";

console.debug(
	"View-Script (view.js) HeroSlider loaded. Initialising Slider...",
);

/**
 * Initializes the slider logic for a specific block instance.
 * @param {HTMLElement} sliderContainer The main block wrapper (.wp-block-gla-heroslider).
 */
function initializeGlaShutterSlider(sliderContainer) {
	// 1. Fetch instance-specific DOM elements
	let nextDom = sliderContainer.querySelector("#next");
	let prevDom = sliderContainer.querySelector("#prev");
	let sliderDom = sliderContainer.querySelector(".slide-list");
	let paginationDom = sliderContainer.querySelector(".slide-pagination");
	let timeDom = sliderContainer.querySelector(".slide-time");
	let isPaused = false;

	// 2. Read Autoplay settings from data attributes
	const isAutoplayEnabled = sliderContainer.dataset.autoplay === "true";
	const dynamicInterval = parseInt(sliderContainer.dataset.interval) || 7000; // Fallback: 7000ms

	// Safety check: Ensure all required elements exist
	if (!nextDom || !prevDom || !sliderDom || !paginationDom) {
		console.error(
			"Missing DOM elements for slider initialization in this instance.",
			sliderContainer,
		);
		return;
	}

	// Variables
	let timeRunning = 1500; // MUST match the CSS transition duration
	let timeAutoNext = dynamicInterval; // Jetzt dynamisch

	// Hide the progress bar (timeDom) if Autoplay is disabled
	if (!isAutoplayEnabled) {
		if (timeDom) timeDom.style.display = "none";
	}

	let numItems = sliderDom.querySelectorAll(".slide-item").length;
	let globalCurrentIndex = 0; // Tracks the logical index of the current slide

	let runTimeOut;
	let runNextAuto;

	/**
	 * Updates the visual state of the pagination buttons.
	 * * @param {number} newIndex The index of the slide to highlight.
	 */
	function updatePaginationClass(newIndex) {
		let allButtons = paginationDom.querySelectorAll("button");
		allButtons.forEach((btn) => btn.classList.remove("active"));

		if (allButtons[newIndex]) {
			allButtons[newIndex].classList.add("active");
		}
	}

	/**
	 * Creates pagination buttons and attaches click events.
	 */
	function initPagination() {
		paginationDom.innerHTML = "";
		for (let i = 0; i < numItems; i++) {
			let button = document.createElement("button");
			button.dataset.index = i;

			if (i === 0) {
				button.classList.add("active");
			}

			button.onclick = function (e) {
				let targetIndex = parseInt(this.dataset.index);
				let activeIndex = globalCurrentIndex;

				if (targetIndex !== activeIndex) {
					// Reset autoplay timer when user interacts with pagination
					if (isAutoplayEnabled) {
						resetAutoPlay();
					}

					// Logic to determine the shortest path and direction
					let distance = targetIndex - activeIndex;
					let clicks = Math.abs(distance);
					let isNext =
						(distance > 0 && clicks <= numItems / 2) ||
						(distance < 0 && clicks > numItems / 2);

					// Temporarily disable navigation during the sequence
					nextDom.style.pointerEvents = "none";
					prevDom.style.pointerEvents = "none";

					/**
					 * Executes multiple slide movements sequentially if the distance > 1.
					 */
					function executeClickSequence(k) {
						if (k >= clicks) {
							// Re-enable navigation after sequence completion
							setTimeout(() => {
								nextDom.style.pointerEvents = "";
								prevDom.style.pointerEvents = "";
							}, timeRunning);
							return;
						}

						setTimeout(() => {
							// Pass false to showSlider to prevent redundant autoplay resets
							if (isNext) {
								showSlider("next", false);
							} else {
								showSlider("prev", false);
							}
							executeClickSequence(k + 1);
						}, timeRunning); // Wait for transition to finish
					}

					// Trigger the first movement immediately
					if (isNext) {
						showSlider("next", false);
					} else {
						showSlider("prev", false);
					}

					if (clicks > 1) {
						executeClickSequence(1);
					} else {
						// Re-enable navigation after a single click
						setTimeout(() => {
							nextDom.style.pointerEvents = "";
							prevDom.style.pointerEvents = "";
						}, timeRunning);
					}
				}
			};
			paginationDom.appendChild(button);
		}
	}

	/**
	 * Resets the Autoplay timer.
	 */
	/* function resetAutoPlay() {
		if (!isAutoplayEnabled) return;
		clearTimeout(runNextAuto);
		runNextAuto = setTimeout(() => {
			nextDom.click();
		}, timeAutoNext);
	} */
	function resetAutoPlay() {
		if (!isAutoplayEnabled || isPaused) return; // Wenn pausiert, kein neuer Timer
		clearTimeout(runNextAuto);
		runNextAuto = setTimeout(() => {
			nextDom.click();
		}, timeAutoNext);
	}

	/**
	 * Core function to handle slider movement.
	 * @param {string} type Direction: "next" or "prev".
	 * @param {boolean} autoPlayReset Whether to restart the autoplay timer.
	 */
	function showSlider(type, autoPlayReset = true) {
		let sliderItemsDom = sliderDom.querySelectorAll(".slide-item");

		if (type === "next") {
			sliderContainer.classList.add("next");
			globalCurrentIndex = (globalCurrentIndex + 1) % numItems;

			// Logic for 'next': Move DOM elements AFTER the animation (1.5s)
			setTimeout(() => {
				sliderItemsDom[0].classList.remove("active");
				sliderDom.appendChild(sliderItemsDom[0]);
				sliderDom
					.querySelector(".slide-item:nth-child(1)")
					.classList.add("active");
				updatePaginationClass(globalCurrentIndex);
			}, timeRunning);
		} else {
			sliderContainer.classList.add("prev");
			globalCurrentIndex = (globalCurrentIndex - 1 + numItems) % numItems;

			// Logic for 'prev': Reorganize DOM IMMEDIATELY on click.
			// This shifts slide 1 to position 2, which CSS then animates.
			sliderItemsDom[0].classList.remove("active");
			sliderDom.prepend(sliderItemsDom[sliderItemsDom.length - 1]);
			sliderDom
				.querySelector(".slide-item:nth-child(1)")
				.classList.add("active");

			updatePaginationClass(globalCurrentIndex);
		}
		// Clean up animation classes
		clearTimeout(runTimeOut);
		runTimeOut = setTimeout(() => {
			sliderContainer.classList.remove("next");
			sliderContainer.classList.remove("prev");
		}, timeRunning);

		if (autoPlayReset) resetAutoPlay();
	}

	// === EVENT LISTENERS FOR ARROWS ===
	nextDom.onclick = function () {
		showSlider("next");
	};

	prevDom.onclick = function () {
		showSlider("prev");
	};

	// === HOVER PAUSE LOGIC ===
	sliderContainer.onmouseenter = function () {
		isPaused = true;
		clearTimeout(runNextAuto); // Stoppt den aktuellen Countdown sofort
		console.debug("Slider paused on hover");
	};

	sliderContainer.onmouseleave = function () {
		isPaused = false;
		// Wenn die Maus den Slider verlässt, starten wir den Autoplay-Timer neu
		if (isAutoplayEnabled) {
			resetAutoPlay();
		}
		console.debug("Slider resumed after hover");
	};

	// === INITIALIZATION ===
	// 1. Ensure the first item is marked active
	sliderDom.querySelector(".slide-item:nth-child(1)").classList.add("active");

	// 2. Setup pagination buttons
	initPagination();

	// 3. Start autoplay if enabled
	if (isAutoplayEnabled) {
		runNextAuto = setTimeout(() => {
			nextDom.click();
		}, timeAutoNext);
	}
}

/**
 * Global initialization on DOM ready.
 */
domReady(() => {
	const sliderContainers = document.querySelectorAll(
		".wp-block-gla-heroslider",
	);

	// Exit if no sliders found or if inside the Block Editor environment
	if (
		!sliderContainers ||
		document.body.classList.contains("block-editor-page")
	)
		return;

	sliderContainers.forEach((container) => {
		// Avoid double initialization (useful for AJAX or caching plugins)
		if (!container.hasAttribute("data-slider-initialized")) {
			initializeGlaShutterSlider(container);
			container.setAttribute("data-slider-initialized", "true");
		}

		/**
		 * Calculates and updates the CSS parallax offset variable based on scroll position.
		 */
		const updateParallax = () => {
			const rect = container.getBoundingClientRect();
			const viewportHeight = window.innerHeight;

			if (rect.top <= viewportHeight && rect.bottom >= 0) {
				const totalDistance = viewportHeight + rect.height;
				const currentProgress = (viewportHeight - rect.top) / totalDistance;
				const maxMovement = viewportHeight * 0.5; // Corresponds to height: 150vh in CSS
				const parallaxValue = -(currentProgress * maxMovement);

				container.style.setProperty("--parallax-offset", `${parallaxValue}px`);
			}
		};

		// 1. Run initial calculation
		updateParallax();

		// 2. Enable visibility class in the next animation frame to ensure smooth entry
		window.requestAnimationFrame(() => {
			container.classList.add("is-ready");
		});

		// 3. Attach scroll listener for parallax
		window.addEventListener("scroll", updateParallax, { passive: true });
	});
});
